'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';

type CalcState = {
  cons: number;
  rate: number;
  days: number;
  workshops: number;
  wsFee: number;
  fixed: number;
  benefit: number;
};

const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

function useCalc(defaults: CalcState) {
  const [state, setState] = React.useState<CalcState>(() => {
    if (typeof window === 'undefined') return defaults;
    try {
      const raw = localStorage.getItem('hr_consult_calc');
      return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
    } catch {
      return defaults;
    }
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hr_consult_calc', JSON.stringify(state));
    }
  }, [state]);

  const cost = state.cons * state.rate * state.days + state.workshops * state.wsFee + state.fixed;
  const payback = state.benefit > 0 ? cost / state.benefit : Infinity;
  const roi12 = cost > 0 ? (state.benefit * 12 - cost) / cost : 0;
  const share = cost > 0 ? state.fixed / cost : 0;

  return { state, setState, cost, payback, roi12, share };
}

function Section({
  id, gray, className, children,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={`${gray ? 'bg-slate-50' : ''} py-20 box-border ${className ?? ''}`}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${className ?? ''}`}>{children}</div>;
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px]">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base">{icon}</div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  const { label, value, onChange, min, step = 1 } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B]">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

export default function HRConsultingPage() {
  const { state, setState, cost, payback, roi12, share } = useCalc({
    cons: 2,
    rate: 45_000,
    days: 20,
    workshops: 4,
    wsFee: 60_000,
    fixed: 150_000,
    benefit: 300_000,
  });

  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    userRequest: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgreementChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, agreed: checked }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      setFormStatus('Произошла ошибка: имя, телефон и email не могут быть пустыми.');
      return;
    }

    setFormStatus('Отправка...');

    const { agreed, ...dataToSend } = formData;

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/hr-consulting-requests`;

    try {
      const payload = {
        ...dataToSend,
        formName: "HR-консалтинг" 
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', userRequest: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setFormStatus(`Произошла ошибка: ${errorMessage}`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  };

  return (
    <main className="bg-white text-slate-900">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20 box-border">
        <div className="mx-auto max-w-6xl pl-1 pr-6">
          <h1 className="text-4xl font-black md:text-5xl">HR-консалтинг</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95 leading-tight">
            Диагностика HR‑процессов, дизайн оргструктуры и политик, KPI/OKR, компенсации и льготы (C&B), обучение и развитие (L&D), HR‑аналитика и автоматизация. Быстрый пилот за 4–6 недель и измеримый эффект.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
            >
              <a href="#calc" className="flex items-center justify-center h-full">
                Оценить проект
              </a>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white"
            >
              <a href="#scope" className="flex items-center justify-center h-full">
                Что берем на себя
              </a>
            </Button>
          </div>
          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon={<span>⚡</span>} value="4–6 недель" label="пилот до результата" />
              <KPI icon={<span>💰</span>} value="−15–25%" label="снижение HR-OPEX*" />
              <KPI icon={<span>📊</span>} value="OKR/KPI" label="метрики и дашборды" />
              <KPI icon={<span>✅</span>} value="ТК РФ" label="процессы и шаблоны" />
            </div>
          </div>
        </div>
      </section>

      <Section id="scope" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">Диагностика и аудит</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Инвентаризация HR/КДП/ЗУП</li>
              <li>Карты SIPOC, RACI, риски и узкие места</li>
              <li>Аудит документов/шаблонов (ТК РФ, 152-ФЗ)</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Оргдизайн и процессы</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Целевые роли и FTE, уровни сервиса (SLA)</li>
              <li>Каталог HR-услуг и регламенты</li>
              <li>Модель компетенций и карьерные лестницы</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Компенсации и льготы</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Классы, вилки, рыночный бенчмаркинг</li>
              <li>Бонусные схемы и LTIP</li>
              <li>Бюджетирование ФОТ</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Эффективность и цели</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>OKR/KPI: каскадирование и циклы оценки</li>
              <li>1:1, PIP, калибровки, девять блоков</li>
              <li>Связка с вознаграждением</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">L&amp;D (обучение)</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Матрицы навыков, планы развития</li>
              <li>Программы лидершипа и наставничества</li>
              <li>Оценка эффективности (Kirkpatrick)</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">HR-аналитика и автоматизация</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Панели мониторинга: текучесть кадров, стоимость найма</li>
              <li>Интеграции 1С:ЗУП/СКУД/портал, ЭДО</li>
              <li>Событийные отчёты и алерты</li>
            </ul>
          </Card>
        </div>
      </Section>

      <Section id="calc">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Оценка проекта</h3>
            <p className="mt-1 text-xs text-slate-500">
              Приблизительная смета и окупаемость. Уточняется на брифе и после диагностики.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LabeledInputNumber label="Консультантов, чел." value={state.cons} onChange={(v) => setState({ ...state, cons: v })} min={1} />
              <LabeledInputNumber label="Ставка, ₽/день" value={state.rate} onChange={(v) => setState({ ...state, rate: v })} min={0} />
              <LabeledInputNumber label="Дней работы, дней" value={state.days} onChange={(v) => setState({ ...state, days: v })} min={1} />
              <LabeledInputNumber label="Воркшопов, шт." value={state.workshops} onChange={(v) => setState({ ...state, workshops: v })} min={0} />
              <LabeledInputNumber label="Цена воркшопа, ₽" value={state.wsFee} onChange={(v) => setState({ ...state, wsFee: v })} min={0} />
              <LabeledInputNumber label="Интеграции/авто, ₽" value={state.fixed} onChange={(v) => setState({ ...state, fixed: v })} min={0} />
              <LabeledInputNumber label="Ожидаемый эффект, ₽/мес" value={state.benefit} onChange={(v) => setState({ ...state, benefit: v })} min={0} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StatCard label="Стоимость проекта" value={cf.format(cost)} />
              <StatCard label="Окупаемость, мес" value={payback === Infinity ? '—' : `${payback.toFixed(1)}`} />
              <StatCard label="ROI за 12 мес" value={`${(roi12 * 100).toFixed(0)}%`} />
              <StatCard label="Доля интеграций" value={`${Math.round(share * 100)}%`} />
            </div>
            <p className="mt-2 text-xs text-slate-500">Итоговые значения зависят от масштаба и сложности работ.</p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">Соглашение об уровне сервиса (SLA) и результаты</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Этап</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Срок</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Артефакты</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Обзор</td>
                    <td className="border-b border-slate-100 px-2 py-2">1–2 нед.</td>
                    <td className="border-b border-slate-100 px-2 py-2">карта процессов, матрица рисков, бэклог</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">План</td>
                    <td className="border-b border-slate-100 px-2 py-2">1–2 нед.</td>
                    <td className="border-b border-slate-100 px-2 py-2">целевые схемы, регламенты, каталоги услуг</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Пилотный проект</td>
                    <td className="border-b border-slate-100 px-2 py-2">2–4 нед.</td>
                    <td className="border-b border-slate-100 px-2 py-2">стандартные операционные процедуры/шаблоны, метрики, обучающие материалы</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Внедрение</td>
                    <td className="border-b border-slate-100 px-2 py-2">1–3 нед.</td>
                    <td className="border-b border-slate-100 px-2 py-2">обучение, обучение, информационные панели в работе</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2">Управление</td>
                    <td className="px-2 py-2">ongoing</td>
                    <td className="px-2 py-2">QBR/отчётность, улучшения и roadmap</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Сроки и артефакты уточняются в SOW/договоре.</p>
          </Card>
        </div>
      </Section>

      <Section gray>
        <h2 className="mb-4 text-2xl font-black">Пакеты</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              name: 'Аудиторский спринт',
              items: ['Диагностика 360° и план', 'Быстрые результаты', '90-дневная дорожная карта'],
              note: '4–6 недель',
            },
            {
              name: 'Трансформация',
              items: ['Перенастройка процессов и ролей', 'Связь целей и показателей эффективности (OKR/KPI) с системой вознаграждения и льгот (C&B)', 'Автоматизация и ЭДО'],
              note: '1–3 месяца',
            },
            {
              name: 'Предприятие',
              items: ['Управление', 'Масштабирование по блокам/регионам', 'Квартальные обзоры (QBR), панели мониторинга, аудит соответствия'],
              note: '3+ месяцев',
            },
          ].map((p) => (
            <Card key={p.name}>
              <h3 className="text-xl font-black">{p.name}</h3>
              <ul className="mt-3 space-y-2 text-black list-disc pl-5">
                {p.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
              <p className="mt-3 text-xs text-black">{p.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <h2 className="mb-4 text-2xl font-black">Процесс</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { n: '1. Бриф', d: 'Цели, ограничения, текущие метрики.' },
            { n: '2. Диагностика', d: 'Интервью, анализ данных/систем, бенчмаркинг.' },
            { n: '3. Дизайн', d: 'Целевые процессы, роли, регламенты, метрики.' },
            { n: '4. Пилот', d: 'Обкатка на 1–2 подразделениях, корректировки.' },
            { n: '5. Запуск', d: 'Внедрение, обучение, дашборды и отчётность.' },
            { n: '6. Поддержка', d: 'Квартальные отчёты, аналитические панели, проверка соблюдения стандартов' },
          ].map((s) => (
            <Card key={s.n}>
              <h3 className="text-lg font-extrabold whitespace-nowrap">{s.n}</h3>
              <p className="mt-1 text-base text-black">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black"><b>Задача:</b> снизить текучесть кадров и ускорить закрытие вакансий в производственном подразделении.</p>
            <p className="mt-1 text-base text-black"><b>Решение:</b> проведен аудит процессов, создан каталог HR-услуг с SLA, внедрены интеграции 1С и ЭДО, реализована система OKR для управления результативностью.</p>
            <p className="mt-1 text-base text-black"><b>Результат:</b> −18% HR-OPEX, TtH −25%, удержание +7 п.п. за 6 месяцев.</p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">FAQ</h3>
            <details className="group mt-2 border-t border-slate-200 py-3" open>
              <summary className="cursor-pointer text-base font-bold">Работаете ли вы по фиксированной цене?</summary>
              <p className="mt-1 text-base text-black">Да, фиксированный объём, документы и срочное ТЗ. Возможны временные и материальные обязательства с ограничением по объёму.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Нужен ли доступ к 1С/порталам?</summary>
              <p className="mt-1 text-base text-black">Да, предоставляется доступ или выгрузки для аналитики и интеграций.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Соблюдение ТК РФ и ПДн?</summary>
              <p className="mt-1 text-base text-black">Все регламенты и шаблоны валидируются на соответствие ТК РФ и 152-ФЗ.</p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta">
        <Card>
          <h3 className="text-xl font-extrabold">Запросить HR-консалтинг</h3>
          <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">

            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Имя</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Как к вам обращаться"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Телефон</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="+7 () ___--__-__"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Запрос</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Например: аудит + OKR/KPI + каталог услуг"
                name="userRequest"
                value={formData.userRequest}
                onChange={handleInputChange}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Email</span>
              <input
                type="email"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="for@example.com"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </label>

            <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600 sm:col-span-2">
              <input
                type="checkbox"
                checked={formData.agreed}
                onChange={(e) => handleAgreementChange(e.target.checked)}
                className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
              />
              <span>
                Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
              </span>
            </label>

            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={handleSubmit}
                className={`inline-block rounded-xl px-6 py-3 text-sm font-bold text-white shadow transition-all duration-200 bg-[#FF7C22] hover:brightness-[105%] hover:-translate-y-[1px]`}
              >
                Отправить
              </button>
              {formStatus && (
                <p className={`mt-2 text-sm font-medium ${formStatus.includes('успешно') ? 'text-green-600' : 'text-red-600'}`}>
                  {formStatus}
                </p>
              )}
            </div>
          </form>
          <p className="mt-2 text-xs text-slate-500">По запросу направим пример SOW и чек-лист диагностики.</p>
        </Card>
      </Section>
    </main>
  );
}